// src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import ExpenseForm from './ExpenseForm';
import ExpenseList from './ExpenseList';
import Summary from './Summary';
import { addExpense, getExpenses, deleteExpense, updateExpense, getExpensesByMonth, getExpensesByYear } from '../api';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LogoutIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16l-4-4m0 0l4-4m-4 4h8" />
  </svg>
);

function Dashboard() {
  const [expenses, setExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isLatestDate, setIsLatestDate] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [annualExpenses, setAnnualExpenses] = useState([]);

  const { logout } = useAuth();
  const navigate = useNavigate();

  // Helper function to format date in YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Function to calculate monthly income, expenses, and profit/loss
  const calculateMonthlyTotals = (expenses) => {
    let monthlyIncome = 0;
    let monthlyExpense = 0;
    expenses.forEach(expense => {
      const amount = parseFloat(expense.amount);
      if (amount > 0) {
        monthlyIncome += amount;
      } else {
        monthlyExpense += amount;
      }
    });
    const monthlyProfitLoss = monthlyIncome + monthlyExpense;

    return { monthlyIncome, monthlyExpense, monthlyProfitLoss };
  };

  // Function to calculate annual income, expenses, and profit/loss
  const calculateAnnualTotals = (expenses) => {
    let annualIncome = 0;
    let annualExpense = 0;
    expenses.forEach(expense => {
      const amount = parseFloat(expense.amount);
      if (amount > 0) {
        annualIncome += amount;
      } else {
        annualExpense += amount;
      }
    });

    const annualProfitLoss = annualIncome + annualExpense;

    return { annualIncome, annualExpense, annualProfitLoss };
  };

  // 使用 useCallback 包装 fetchExpenses
  const fetchExpenses = useCallback(async (date) => {
    const formattedDate = formatDate(date);
    const data = await getExpenses(formattedDate);
    setExpenses(data);
    const today = new Date();
    setIsLatestDate(date.toDateString() === today.toDateString());
  }, []);

  // 使用 useEffect
  useEffect(() => {
    fetchExpenses(currentDate);
    fetchMonthlyExpenses(selectedMonth, selectedYear);
    fetchAnnualExpenses(selectedYear);
  }, [currentDate, selectedMonth, selectedYear, fetchExpenses]);

  // Fetch monthly expenses
  const fetchMonthlyExpenses = async (month, year) => {
    const data = await getExpensesByMonth(month, year);
    setMonthlyExpenses(data);
  };

  // Fetch annual expenses
  const fetchAnnualExpenses = async (year) => {
    const data = await getExpensesByYear(year);
    setAnnualExpenses(data);
  };

  // 添加支出记录
  const handleAddExpense = async (expense) => {
    const newExpense = await addExpense(expense);
    setExpenses([...expenses, newExpense]);
    setShowModal(false);
    fetchExpenses(currentDate);
    fetchMonthlyExpenses(selectedMonth, selectedYear);
    fetchAnnualExpenses(selectedYear);
  };

  // 删除支出记录
  const confirmDeleteExpense = async () => {
    if (expenseToDelete) {
      await deleteExpense(expenseToDelete);
      setExpenses(expenses.filter(expense => expense.id !== expenseToDelete));
      setShowDeleteConfirm(false);
      fetchMonthlyExpenses(selectedMonth, selectedYear);
      fetchAnnualExpenses(selectedYear);
    }
  };

  // 删除支出记录，打开确认模态框
  const handleDeleteExpense = (id) => {
    setExpenseToDelete(id);
    setShowDeleteConfirm(true);
  };

  // 编辑支出记录
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setShowModal(true);
  };

  // 更新支出记录
  const handleUpdateExpense = async (updatedExpense) => {
    await updateExpense(updatedExpense);
    setExpenses(expenses.map(expense => (expense.id === updatedExpense.id ? updatedExpense : expense)));
    setEditingExpense(null);
    setShowModal(false);
    fetchExpenses(currentDate);
    fetchMonthlyExpenses(selectedMonth, selectedYear);
    fetchAnnualExpenses(selectedYear);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingExpense(null);
    setShowModal(false);
  };

  // 更改排序
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // 根据排序字段和顺序排序数据
  const sortedExpenses = [...expenses].sort((a, b) => {
    const aValue = sortBy === 'date' ? new Date(a[sortBy]) : parseFloat(a[sortBy]);
    const bValue = sortBy === 'date' ? new Date(b[sortBy]) : parseFloat(b[sortBy]);

    if (sortOrder === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  // 切换到前一天
  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  // 切换到后一天
  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  // Handlers for month and year changes
  const handleMonthChange = (event) => {
    setSelectedMonth(parseInt(event.target.value, 10));
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const { monthlyIncome, monthlyExpense, monthlyProfitLoss } = calculateMonthlyTotals(monthlyExpenses);
  const { annualIncome, annualExpense, annualProfitLoss } = calculateAnnualTotals(annualExpenses);

  return (
    <div className="container">
      <header style={{ display: 'flex', alignItems: 'center' }}>
        <button className="logout-button" onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <LogoutIcon />
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h1>&emsp;钱来！钱来！</h1>
          <p className="user-info">钱从四面八方来，财自鸡毛蒜皮起</p>
        </div>
      </header>
      <div className="mincontainer">
      <Summary // 使用 Summary 组件
          selectedYear={selectedYear}
          handleYearChange={handleYearChange}
          annualIncome={annualIncome}
          annualExpense={annualExpense}
          annualProfitLoss={annualProfitLoss}
          selectedMonth={selectedMonth}
          handleMonthChange={handleMonthChange}
          monthlyIncome={monthlyIncome}
          monthlyExpense={monthlyExpense}
          monthlyProfitLoss={monthlyProfitLoss}
        />
        <div className="section">
          <h2 className="section-title">添加收支记录</h2>
          <ExpenseForm addExpense={handleAddExpense} />
        </div>
      </div>

      <div className="section daylist">
        <h2 className="section-title">
          <i><u>{currentDate.toLocaleDateString()}</u></i> 收支记录
        </h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <button className="check-btn1" onClick={goToPreviousDay}>前一天</button>
          {!isLatestDate && <button className="check-btn2" onClick={goToNextDay}>后一天</button>}
        </div>
        <ExpenseList
          expenses={sortedExpenses}
          deleteExpense={handleDeleteExpense}
          editExpense={handleEditExpense}
          sortBy={sortBy}
          sortOrder={sortOrder}
          handleSort={handleSort}
        />
        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <ExpenseForm
                editingExpense={editingExpense}
                handleUpdateExpense={handleUpdateExpense}
                handleCancelEdit={handleCancelEdit}
              />
            </div>
          </div>
        )}
        {showDeleteConfirm && (
          <div className="modal-delete">
            <div className="modal-delete-content">
              <h3>确认删除</h3>
              <p>您确定要删除此支出记录吗？</p>
              <div className="modal-delete-buttons">
                <button className='modal-delete-confirm' onClick={confirmDeleteExpense}>确认</button>
                <button className='modal-delete-cancel' onClick={() => setShowDeleteConfirm(false)}>取消</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
